import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  readPremiumIntent,
  clearPremiumIntent,
} from "../../utils/premiumIntent";
import { popScroll } from "../../utils/scrollMemory";
import BrandLockup from "../common/BrandLockup";
import { useLanguage } from "../../i18n/LanguageContext";

const DEV_MODE = process.env.REACT_APP_DEV_MODE === "true";
const PASSWORD_MIN = 8;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function PremiumBanner() {
  const { t } = useLanguage();
  return (
    <div
      role="status"
      className="mb-4 rounded-xl px-4 py-2.5 text-sm text-white shadow-sm"
      style={{
        background: "linear-gradient(135deg, #9B30FF 0%, #6A0DAD 100%)",
      }}
    >
      {t("auth.premiumBanner")}
    </div>
  );
}

const AuthDialog = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register, fetchUser } = useAuth();
  const { t } = useLanguage();

  const queryMode = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("mode") === "signup" ? "signup" : "login";
  }, [location.search]);

  const prefillEmail = useMemo(() => {
    return new URLSearchParams(location.search).get("email") || "";
  }, [location.search]);

  useEffect(() => {
    document.title = queryMode === "signup" ? t("auth.signupTitle") : t("auth.loginTitle");
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute(
      "content",
      queryMode === "signup"
        ? t("auth.signupDescription")
        : t("auth.loginDescription")
    );
  }, [queryMode, t]);

  const intent = useMemo(() => readPremiumIntent(), []);
  const [email, setEmail] = useState(prefillEmail);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const redirectAfterAuth = () => {
    const intentNow = readPremiumIntent();
    if (intentNow?.plan) {
      const plan = encodeURIComponent(intentNow.plan);
      clearPremiumIntent();
      navigate(`/app/billing/checkout?plan=${plan}`, { replace: true });
    } else {
      navigate("/app", { replace: true });
    }
  };

  const validate = () => {
    if (!EMAIL_RE.test(email)) {
      setError(t("auth.invalidEmail"));
      return false;
    }
    if (password.length < PASSWORD_MIN) {
      setError(t("auth.shortPassword", { count: PASSWORD_MIN }));
      return false;
    }
    return true;
  };

  const startAuth = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (DEV_MODE) {
        const user = await fetchUser();
        if (!user) {
          setError(t("auth.devFailed"));
          return;
        }
        redirectAfterAuth();
        return;
      }
      if (!validate()) return;
      if (queryMode === "signup") {
        await register(email, password);
      } else {
        await login(email, password);
      }
      redirectAfterAuth();
    } catch (err) {
      setError(err?.message || t("auth.failed"));
    } finally {
      setLoading(false);
    }
  };

  const handleBackHome = (e) => {
    e.preventDefault();
    const y = popScroll();
    navigate("/", { replace: false });
    if (typeof y === "number") {
      requestAnimationFrame(() => {
        window.scrollTo({ top: y, behavior: "auto" });
      });
    }
  };

  const switchMode = (next) => {
    setError("");
    const params = new URLSearchParams(location.search);
    params.set("mode", next);
    navigate(`/auth?${params.toString()}`, { replace: true });
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center px-4 py-6 sm:py-10">
      <div className="w-full max-w-md">
        <div className="mb-4">
          <Link
            to="/"
            onClick={handleBackHome}
            className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 rounded-md px-1 py-0.5"
          >
            <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path
                fillRule="evenodd"
                d="M12.707 4.293a1 1 0 010 1.414L8.414 10l4.293 4.293a1 1 0 01-1.414 1.414l-5-5a1 1 0 010-1.414l5-5a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
            {t("auth.backHome")}
          </Link>
        </div>

        <div className="text-center mb-6">
          <BrandLockup />
          <p className="text-gray-500 text-sm mt-1">{t("auth.subtitle")}</p>
        </div>

        {intent?.plan && <PremiumBanner />}

        <div className="card p-6 sm:p-8">
          <div role="tablist" aria-label="Authentication mode" className="flex mb-6">
            <button
              role="tab"
              type="button"
              aria-selected={queryMode === "login"}
              onClick={() => switchMode("login")}
              className={`flex-1 pb-2 text-sm font-medium border-b-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 ${
                queryMode === "login"
                  ? "border-purple-600 text-purple-700"
                  : "border-transparent text-gray-600 hover:text-gray-800"
              }`}
            >
              {t("auth.login")}
            </button>
            <button
              role="tab"
              type="button"
              aria-selected={queryMode === "signup"}
              onClick={() => switchMode("signup")}
              className={`flex-1 pb-2 text-sm font-medium border-b-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 ${
                queryMode === "signup"
                  ? "border-purple-600 text-purple-700"
                  : "border-transparent text-gray-600 hover:text-gray-800"
              }`}
            >
              {t("auth.signup")}
            </button>
          </div>

          <div aria-live="assertive" aria-atomic="true" className="min-h-[1.25rem] mb-4">
            {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
          </div>

          <form onSubmit={startAuth} className="space-y-4" noValidate>
            <div>
              <label htmlFor="auth-email" className="block text-sm font-medium text-gray-700 mb-1">
                {t("auth.email")}
              </label>
              <input
                id="auth-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-field"
                placeholder={t("auth.emailPlaceholder")}
                required
              />
            </div>
            <div>
              <label htmlFor="auth-password" className="block text-sm font-medium text-gray-700 mb-1">
                {t("auth.password")}
              </label>
              <div className="relative">
                <input
                  id="auth-password"
                  type={showPassword ? "text" : "password"}
                  autoComplete={queryMode === "login" ? "current-password" : "new-password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-field pr-12"
                  placeholder={queryMode === "login" ? t("auth.passwordLoginPlaceholder") : t("auth.passwordSignupPlaceholder")}
                  required
                  minLength={PASSWORD_MIN}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? t("auth.hidePassword") : t("auth.showPassword")}
                  aria-pressed={showPassword}
                  className="absolute inset-y-0 right-0 px-3 text-gray-500 hover:text-purple-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 rounded-r-xl"
                >
                  {showPassword ? t("auth.hidePassword") : t("auth.showPassword")}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? t("auth.pleaseWait") : queryMode === "login" ? t("auth.login") : t("auth.createAccount")}
            </button>
          </form>
        </div>

        <p className="mt-6 text-center text-xs text-gray-600">
          {t("auth.termsNotice")}
        </p>
      </div>
    </div>
  );
};

export default AuthDialog;
